<template>
  <gg-modal :is-active="isActive" @close="close">
    <template v-slot:toolbar>
      Create Mailbox
    </template>
    <template v-slot:body>
      <v-form class="pt-2" @submit.prevent="create">
        <v-row>
          <v-col cols="12" class="py-0">
            <!-- Without `serviceId` the picker spans every service, so the
                 domain determines which cPanel account the mailbox is created
                 on. With it, only that account's domains are offered. -->
            <gg-select
              v-model="domainKey"
              label="Domain"
              placeholder="Select a domain"
              :items="domainOptions"
              :loading="domainsLoading"
              :disabled="domainsLoading"
              no-data-text="No domains are available on your hosting plans."
              v-validate="'required'"
              data-vv-name="domain"
              data-vv-as="Domain"
              :error-messages="errors.collect('domain')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <gg-text-field
              outlined
              dense
              label="Mailbox Name"
              placeholder="ex. yourname"
              autocomplete="new-password"
              v-model="model.email"
              v-validate="'required'"
              name="Mailbox"
              :error-messages="errors.collect('Mailbox')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <gg-input-label>Password</gg-input-label>
            <compact-password
              outlined
              autocomplete="new-password"
              with-generator
              v-model="model.password"
              v-validate="'required'"
              data-vv-name="password"
              data-vv-as="Password"
              :error-messages="errors.collect('password')"
            />
          </v-col>
          <v-col cols="12" class="py-0">
            <v-switch
              color="green"
              class="mt-0"
              label="Send welcome email with mail client configuration instructions."
              v-model="sendSettings"
            />
          </v-col>
          <v-col v-if="address" cols="12" class="pt-0">
            <p class="body-2 mb-0">This will create <strong>{{ address }}</strong>.</p>
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <gg-btn secondary @click="close">Cancel</gg-btn>
      <gg-btn primary :loading="loading" :disabled="domainsLoading" @click="create">
        Create Mailbox
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CompactPassword from '@/components/Common/password/CompactPassword'
import { MAILBOX_QUOTA_MB } from '@/consts/email'

export default {
  name: 'CreateMailboxModal',
  components: {
    CompactPassword,
  },
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    // Set when the form is opened from inside an email account, where the
    // service is already known.
    serviceId: {
      type: [String, Number],
      default: null,
    },
    // Preselected when it is among the options — an addon domain opened as
    // its own account creates mailboxes on itself.
    defaultDomain: {
      type: String,
      default: '',
    },
  },
  data () {
    return {
      model: {
        email: '',
        password: '',
      },
      domainKey: '',
      sendSettings: true,
      loading: false,
      // `loadDomainsForService` has no store status of its own, unlike the
      // every-service `loadDomains`.
      serviceDomainsLoading: false,
    }
  },
  computed: {
    ...mapGetters('emailHub', [
      'mailboxDomainOptions',
      'mailboxDomainsStatus',
      'mailboxDomainFor',
      'mailboxDomainsForService',
    ]),
    hasService () {
      return this.serviceId !== null && this.serviceId !== undefined
    },
    // Values keep the `serviceId:domain` shape either way, so the rest of the
    // form resolves the choice through `mailboxDomainFor` the same way.
    domainOptions () {
      if (!this.hasService) return this.mailboxDomainOptions
      return this.mailboxDomainsForService(this.serviceId)
        .map(domain => ({ text: domain, value: `${this.serviceId}:${domain}` }))
    },
    domainsLoading () {
      return this.hasService ? this.serviceDomainsLoading : this.mailboxDomainsStatus === 'loading'
    },
    selectedDomain () {
      return this.mailboxDomainFor(this.domainKey)
    },
    address () {
      if (!this.model.email || !this.selectedDomain) return ''
      return `${this.model.email}@${this.selectedDomain.domain}`
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadDomains', 'loadDomainsForService', 'createMailbox', 'sendClientSettings']),
    ...mapActions(['reportSuccess', 'reportError']),
    close () {
      this.$emit('close')
    },
    reset () {
      this.model.email = ''
      this.model.password = ''
      this.sendSettings = true
      this.$validator.reset()
    },
    async loadOptions () {
      if (!this.hasService) {
        await this.loadDomains()
        return
      }
      this.serviceDomainsLoading = true
      try {
        await this.loadDomainsForService(this.serviceId)
      } finally {
        this.serviceDomainsLoading = false
      }
    },
    async create () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const domain = this.selectedDomain
      if (!domain) return

      this.loading = true
      const account = `${this.model.email}@${domain.domain}`

      try {
        await this.createMailbox({
          serviceId: domain.serviceId,
          email: this.model.email,
          domain: domain.domain,
          password: this.model.password,
          quota: String(MAILBOX_QUOTA_MB),
        })
      } catch (e) {
        this.loading = false
        this.reportError(e)
        return
      }

      // The mailbox exists from here on, so a failed welcome email is a warning
      // rather than a failed creation — reporting it as an error would tell the
      // customer nothing was created when something was.
      if (this.sendSettings) {
        try {
          await this.sendClientSettings({ serviceId: domain.serviceId, account })
        } catch (e) {
          console.warn('CreateMailboxModal: mailbox created but welcome email failed', e)
        }
      }

      this.loading = false
      this.reportSuccess('Mailbox was created successfully')
      this.reset()
      this.$emit('created', domain.serviceId)
      this.close()
    },
  },
  watch: {
    // GGModal is `eager`, so this component exists before it is ever opened.
    // Load the domains on first open rather than on mount.
    isActive (open) {
      if (!open) return
      this.loadOptions().catch(e => this.reportError(e))
    },
    // A choice from another account must not survive a change of account.
    serviceId () {
      this.domainKey = ''
    },
    // Preselect once the options arrive, the way DomainSelect does, so a
    // single-service customer never has to touch the picker.
    domainOptions (options) {
      if (options.some(option => option.value === this.domainKey)) return
      const preferred = this.defaultDomain &&
        options.find(option => option.text === this.defaultDomain)
      const first = preferred || options.find(option => option.value)
      if (first) this.domainKey = first.value
    },
  },
}
</script>
